import React, {useContext, useEffect, useRef} from "react";
import {GlobalDataContext} from "../../../contexts/GlobalDataContext";
import {RegionSelectedContext, RegionValue, YearSelectedType} from "../../../contexts/RegionSelectedContext";
import PillRow, {HeaderPillRow, PillRecord} from "../../pills/PillRow";
import EuropeRecord from "../../../models/EuropeRecord";
import Branch from "../../../interfaces/Branch";
import LatamRecord from "../../../models/LatamRecord";
import styled from "styled-components";

const PillsTable = styled.div`
  display: flex;
  min-width: 0;

  @media (max-height: 900px) {
    font-size: 0.8rem;
  }

  .table {
    flex: 5;
    display: flex;
    flex-direction: column;
    min-width: 0;
    min-height: 0;
  }

  /* WIDE DESKTOP (>=1024px): text and table sit side by side, so fill the
     available height and let only the rows scroll, keeping the outer tab from
     scrolling while the MAPFRE row can always be revealed (see auto-scroll
     effect below). Below 1024px the columns stack and the whole tab scrolls. */
  @media (min-width: 1024px) {
    flex: 1;
    min-height: 0;

    .rowsScroll {
      position: relative;
      flex: 1;
      min-height: 0;
      /* Safety cap so the rows always scroll even if the flex height does not
         propagate; the outer tab clips any excess so it never scrolls. */
      max-height: calc(100vh - 360px);
      overflow-y: auto;
    }
  }
`;

const parseEuropeRecords = (records: EuropeRecord[], year: YearSelectedType): PillRecord[] => {
    const sorted = records
        .filter((record: EuropeRecord) => record.year === year.year && record.niiff === year.niiff)
        .filter((record: EuropeRecord) => record.branch === Branch.TOTAL)
        .sort((r1: EuropeRecord, r2: EuropeRecord) => r2.premiums_current_year - r1.premiums_current_year);

    // Older years only have 15 ranked groups; the coming years carry 20. Drive the
    // count off the data so both situations render correctly.
    return sorted
        .slice(0, sorted.length > 15 ? 20 : 15)
        .map((x: EuropeRecord) => {
            return {
                company_group: x.company_group,
                premiums: x.premiums_current_year,
                registered_office: x.registered_office
            };
        });
};

const parseLatamRecords = (records: LatamRecord[], year: YearSelectedType, region: RegionValue): PillRecord[] => {
    return records
        .filter((record: LatamRecord) => record.year === year.year && record.niiff === year.niiff)
        .filter((record: LatamRecord) => record.branch === Branch.TOTAL)
        .sort((r1: LatamRecord, r2: LatamRecord) => r2.premiums - r1.premiums)
        .slice(0, region === "latam" ? 15 : 20)
        .map((x: LatamRecord) => {
            return {
                company_group: x.company_group,
                premiums: x.premiums,
                registered_office: x.registered_office
            };
        });
};


interface TotalRankingPillsComponentProps {
    active: boolean,
}

const TotalRankingPillsComponent: React.FC<TotalRankingPillsComponentProps> = ({ active }) => {
    const rowsRef = useRef<HTMLDivElement>(null);
    // @ts-ignore
    const {europeRecords, latamRecords, reaseguroRecords} = useContext(GlobalDataContext);
    const { yearSelected, regionSelected } = useContext(RegionSelectedContext);

    const hasData = Object.keys(europeRecords).length !== 0;

    const records: PillRecord[] = !hasData
        ? []
        : (regionSelected === "eu"
            ? parseEuropeRecords(europeRecords, yearSelected)
            : (regionSelected === "latam"
                ? parseLatamRecords(latamRecords, yearSelected, regionSelected)
                : parseLatamRecords(reaseguroRecords, yearSelected, regionSelected)
            ));

    useEffect(() => {
        // Only the wide (>=1024px) layout has per-table scrolling; below that the
        // columns stack and the whole tab scrolls, so no auto-scroll is needed.
        if (!active || window.innerWidth < 1024) return;

        const container = rowsRef.current;
        if (!container) return;

        // Defer until layout is available (the tab body may have just become visible).
        const frame = requestAnimationFrame(() => {
            const mapfreRow = container.querySelector<HTMLElement>('.mapfre-row');
            if (!mapfreRow) return;
            container.scrollTop =
                mapfreRow.offsetTop - container.clientHeight / 2 + mapfreRow.clientHeight / 2;
        });

        return () => cancelAnimationFrame(frame);
    }, [records, active]);

    if (!hasData) {
        return (<div/>);
    }

    const currency = regionSelected === 'eu' ? 'M€' : 'M USD';

    return (
        <PillsTable>
            <div className={'table'}>
                <HeaderPillRow
                    year={ yearSelected }
                    isDark={false}
                    currency={ currency }
                />
                <div className={'rowsScroll'} ref={ rowsRef }>
                    {records.map((record, i) => {
                        return (
                            <PillRow
                                number={i + 1}
                                record={record}
                                isDark={false}
                                key={record.company_group}
                            />
                        );
                    })}
                </div>
            </div>
        </PillsTable>
    );
};

export default TotalRankingPillsComponent
